import {
  ChangeDetectorRef,
  Directive,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { FormGroup, FormGroupDirective } from '@angular/forms';
import { Actions, getValue, ofActionDispatched, Store } from '@ngxs/store';
import { Observable, Subject } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  take,
  takeUntil,
} from 'rxjs/operators';

import { QuoteActions } from './state/quote.actions';

/**
 * This directive should be made generic and have its own state
 * Not making it generic was a mistake, and we need to address that later.
 *
 * The quote directive is designed to automatically sync the form data and state
 * to NGXS state management. In addition, it can be used in different forms throughout the purchase path.
 * It's only available for form tag and has 2 inputs.
 *
 *    1. quote => specify the IFormState, part of IQuote. e.g: qqcForm
 *    2. debounce (optional) => by default, it adds 100ms as debounce time (a delay between dispatching the value and user input)
 *
 * @example
 * <form [formGroup]="formGroup" quote="qqcForm" debounce="100">
 *    ...
 * </form>
 *
 * @export
 * @class QuoteDirective
 * @implements {OnInit}
 * @implements {OnDestroy}
 */
// eslint-disable-next-line @angular-eslint/directive-selector
@Directive({ selector: '[quote]' })
export class QuoteDirective implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private updating = false;

  /**
   * Expects the form name, part of the IQuote interface.
   * Throws if form is not defined.
   * e.g.: quote.qqcForm or quote.detailsForm
   */
  @Input('quote') path!: string;
  @Input() model!: string;
  @Input() debounce = 100;

  private get form(): FormGroup {
    return this.formGroupDirective.form;
  }

  constructor(
    private actions$: Actions,
    private store: Store,
    private formGroupDirective: FormGroupDirective,
    private cd: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.formResetHandler();
    this.quoteInputHandler();
    this.isDirtyHandler();
    this.isDisabledHandler();
    this.initialStateHandler();
    this.changesHandler();
  }

  private changesHandler() {
    if (this.formGroupDirective.valueChanges) {
      this.formGroupDirective.valueChanges
        .pipe(this.debounceChange())
        .subscribe(() => this.updateFormState(true));
    }

    if (this.formGroupDirective.statusChanges) {
      this.formGroupDirective.statusChanges
        .pipe(distinctUntilChanged(), this.debounceChange())
        .subscribe((status: string) => {
          this.store.dispatch(
            new QuoteActions.UpdateFormStatus({
              status,
              path: this.path,
            })
          );
        });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private updateFormState(withFormStatus?: boolean): void {
    if (this.updating) {
      return;
    }

    const value = this.formGroupDirective.control.getRawValue();
    const actions: any[] = [
      new QuoteActions.UpdateFormValue(value, this.model),
      new QuoteActions.UpdateFormDirty({
        path: this.path,
        dirty: this.formGroupDirective.dirty,
      }),
      new QuoteActions.UpdateFormErrors({
        path: this.path,
        errors: this.formGroupDirective.errors,
      }),
    ];

    if (withFormStatus) {
      actions.push(
        new QuoteActions.UpdateFormStatus({
          status: this.formGroupDirective.status,
          path: this.path,
        })
      );
    }

    this.updating = true;
    this.store.dispatch(actions).subscribe({
      error: () => (this.updating = false),
      complete: () => (this.updating = false),
    });
  }

  // On first state change, sync form model, status and dirty with state
  // From existing data on the state to ng form
  private initialStateHandler() {
    this.getStateStream(`quote.${this.model}`)
      .pipe(take(1))
      .subscribe(() => {
        this.store.dispatch([
          new QuoteActions.UpdateFormValue(this.form.getRawValue(), this.model),
          new QuoteActions.UpdateFormStatus({
            path: this.path,
            status: this.form.status,
          }),
          new QuoteActions.UpdateFormDirty({
            path: this.path,
            dirty: this.form.dirty,
          }),
        ]);
      });
  }

  /** From the state dirty to ng form */
  private isDirtyHandler() {
    this.getStateStream(`${this.path}.dirty`)
      .pipe(takeUntil(this.destroy$))
      .subscribe((dirty) => {
        if (this.form.dirty === dirty || typeof dirty !== 'boolean') {
          return;
        }

        if (dirty) {
          this.form.markAsDirty();
        } else {
          this.form.markAsPristine();
        }

        this.cd.markForCheck();
      });
  }

  /** From the state dirty to ng form */
  private isDisabledHandler() {
    this.getStateStream(`${this.path}.disabled`)
      .pipe(takeUntil(this.destroy$))
      .subscribe((disabled) => {
        if (this.form.disabled === disabled || typeof disabled !== 'boolean') {
          return;
        }

        if (disabled) {
          this.form.disable();
        } else {
          this.form.enable();
        }

        this.cd.markForCheck();
      });
  }

  /**
   * From state to angular form
   * This input is only taken once, if the application refreshes,
   * Otherwise it re-triggers all form events when user navigates
   * using back/forward button, which cause some side-effects.
   * Also setting the emitEvent as false to prevent unwanted
   * Angular form event propagation.
   */
  private quoteInputHandler() {
    this.getStateStream(`quote.${this.model}`)
      .pipe(takeUntil(this.destroy$))
      .subscribe((quote) => {
        if (this.updating || !quote) {
          return;
        }

        this.form.patchValue(quote, { emitEvent: false });
        this.cd.markForCheck();
      });
  }

  /** Handles reset form, should only be triggered at the end of purchase */
  private formResetHandler() {
    this.actions$
      .pipe(
        ofActionDispatched(QuoteActions.ResetForm),
        takeUntil(this.destroy$)
      )
      .subscribe(({ payload: value }: QuoteActions.ResetForm) => {
        this.form.reset(value);
        this.updateFormState(true);
        this.cd.markForCheck();
      });
  }

  private debounceChange() {
    const skipDebounceTime =
      this.formGroupDirective.control.updateOn !== 'change' ||
      this.debounce < 0;

    return skipDebounceTime
      ? (change: Observable<any>) => change.pipe(takeUntil(this.destroy$))
      : (change: Observable<any>) =>
          change.pipe(debounceTime(this.debounce), takeUntil(this.destroy$));
  }

  private getStateStream(path: string) {
    return this.store
      .select((state) => getValue(state, path))
      .pipe(takeUntil(this.destroy$));
  }
}
